import { useEffect, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";
import { products as productsApi, PRODUCT_STATUSES, PRODUCT_TYPES, type ProductDetail } from "@/features/catalog/api/catalog.api";
import { toApiDateTime, dateOnly } from "@/utils/dates";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { TextField, TextAreaField, SelectField, NumberField, DateField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";

const num = z.number().nullable();

const schema = z.object({
  name: z.string().trim().min(1, "Product name is required.").max(FIELD_MAX.product.name, tooLong("Name", FIELD_MAX.product.name)),
  type: z.string(),
  sku: z.string().max(FIELD_MAX.product.sku, tooLong("SKU", FIELD_MAX.product.sku)),
  serialNumber: z.string().max(FIELD_MAX.product.serialNumber, tooLong("Serial number", FIELD_MAX.product.serialNumber)),
  licenseKey: z.string().max(FIELD_MAX.product.licenseKey, tooLong("License key", FIELD_MAX.product.licenseKey)),
  modelNumber: z.string().max(FIELD_MAX.product.modelNumber, tooLong("Model number", FIELD_MAX.product.modelNumber)),
  manufacturer: z.string().max(FIELD_MAX.product.manufacturer, tooLong("Manufacturer", FIELD_MAX.product.manufacturer)),
  barcode: z.string().max(FIELD_MAX.product.barcode, tooLong("Barcode", FIELD_MAX.product.barcode)),
  status: z.string(),
  description: z.string().max(FIELD_MAX.product.description, tooLong("Description", FIELD_MAX.product.description)),
  cost: num,
  priceLevel1: num,
  priceLevel2: num,
  priceLevel3: num,
  priceLevel4: num,
  inventoryQty: num,
  dateInStock: z.string(),
  warrantyExpiresAt: z.string(),
  dateSold: z.string(),
  notes: z.string().max(FIELD_MAX.product.notes, tooLong("Notes", FIELD_MAX.product.notes)),
});

type FormValues = z.infer<typeof schema>;


function toForm(p: ProductDetail): FormValues {
  return {
    name: p.name,
    type: p.type,
    sku: p.sku ?? "",
    serialNumber: p.serialNumber ?? "",
    licenseKey: p.licenseKey ?? "",
    modelNumber: p.modelNumber ?? "",
    manufacturer: p.manufacturer ?? "",
    barcode: p.barcode ?? "",
    status: p.status,
    description: p.description ?? "",
    cost: p.cost,
    priceLevel1: p.priceLevel1,
    priceLevel2: p.priceLevel2,
    priceLevel3: p.priceLevel3,
    priceLevel4: p.priceLevel4,
    inventoryQty: p.inventoryQty,
    dateInStock: dateOnly(p.dateInStock),
    warrantyExpiresAt: dateOnly(p.warrantyExpiresAt),
    dateSold: dateOnly(p.dateSold),
    notes: p.notes ?? "",
  };
}

export default function ProductEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/products");
  const isEdit = id !== undefined;
  const confirm = useConfirm();

  const [base, setBase] = useState<ProductDetail>(blankProduct());
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toForm(blankProduct()),
  });

  useAutoFocus(form, "name", !isEdit);

  useEffect(() => {
    let cancelled = false;
    if (isEdit && id) {
      productsApi.get(id)
        .then((p) => { if (!cancelled) { setBase(p); form.reset(toForm(p)); setLoading(false); } })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setError(e instanceof Error ? e.message : "Failed to load product.");
          setLoading(false);
        });
    }
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  function buildDto(v: FormValues): ProductDetail {
    const text = (s: string) => s.trim() || null;
    return {
      ...base,
      name: v.name.trim(),
      type: v.type,
      sku: text(v.sku),
      serialNumber: text(v.serialNumber),
      licenseKey: text(v.licenseKey),
      modelNumber: text(v.modelNumber),
      manufacturer: text(v.manufacturer),
      barcode: text(v.barcode),
      status: v.status,
      description: text(v.description),
      notes: text(v.notes),
      cost: v.cost ?? 0,
      priceLevel1: v.priceLevel1 ?? 0,
      priceLevel2: v.priceLevel2 ?? 0,
      priceLevel3: v.priceLevel3 ?? 0,
      priceLevel4: v.priceLevel4 ?? 0,
      inventoryQty: Math.max(0, Math.floor(v.inventoryQty ?? 0)),
      dateInStock: toApiDateTime(v.dateInStock),
      warrantyExpiresAt: toApiDateTime(v.warrantyExpiresAt),
      dateSold: toApiDateTime(v.dateSold),
    };
  }

  async function onValid(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    setError(null);
    try {
      const dto = buildDto(v);
      if (isEdit && id) {
        await productsApi.update(id, dto);
        const fresh = await productsApi.get(id);
        setBase(fresh);
        form.reset(toForm(fresh));
        toast.success("Product updated");
      } else {
        const created = await productsApi.create(dto);
        toast.success("Product created");
        navigate(`/manage/products/${created.id}`, { replace: true, state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  async function onDelete() {
    if (!id) return;
    if (!(await confirm({ description: "Delete this product? Campaigns referencing it lose the link.", confirmLabel: "Delete", destructive: true }))) return;
    setError(null);
    try {
      await productsApi.remove(id);
      navigate(backTo, { replace: true });
    } catch (e) {
      toastApiError(e, "Delete failed.");
    }
  }

  const isHardware = form.watch("type") !== "SoftwareLicense";
  const saving = form.formState.isSubmitting;
  useUnsavedChangesWarning(form.formState.isDirty);

  return (
    <>
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back to products
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Edit product" : "New product"}
        </h1>
        {isEdit && (
          <Button type="button" variant="destructive" onClick={onDelete}>
            Delete product
          </Button>
        )}
      </header>

      {notFound ? (
        <Notice tone="info">Product not found.</Notice>
      ) : loading ? (
        <Loading>Loading product…</Loading>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="space-y-6">
            <Panel title="Identity">
              <Grid>
                <TextField control={form.control} name="name" label="Name" required />
                <SelectField
                  control={form.control}
                  name="type"
                  label="Type"
                  options={PRODUCT_TYPES.map((t) => [t, t === "SoftwareLicense" ? "Software license" : "Hardware"])}
                />
                <TextField control={form.control} name="sku" label="SKU" />
                {isHardware ? (
                  <TextField control={form.control} name="serialNumber" label="Serial number" />
                ) : (
                  <TextField control={form.control} name="licenseKey" label="License key" />
                )}
                <TextField control={form.control} name="modelNumber" label="Model number" />
                <TextField control={form.control} name="manufacturer" label="Manufacturer" />
                <TextField control={form.control} name="barcode" label="Barcode" />
                <SelectField
                  control={form.control}
                  name="status"
                  label="Status"
                  options={PRODUCT_STATUSES.map((s) => [s, s])}
                />
              </Grid>
              <div className="mt-4">
                <TextAreaField control={form.control} name="description" label="Description" rows={2} />
              </div>
            </Panel>

            <Panel title="Pricing — four tiers">
              <Grid cols={3}>
                <NumberField control={form.control} name="cost" label="Cost" prefix="$" min={0} />
                <NumberField control={form.control} name="priceLevel1" label="Price Level 1 (Retail)" prefix="$" min={0} />
                <NumberField control={form.control} name="priceLevel2" label="Price Level 2" prefix="$" min={0} />
                <NumberField control={form.control} name="priceLevel3" label="Price Level 3" prefix="$" min={0} />
                <NumberField control={form.control} name="priceLevel4" label="Price Level 4" prefix="$" min={0} />
              </Grid>
            </Panel>

            <Panel title="Inventory & lifecycle">
              <Grid cols={3}>
                <NumberField control={form.control} name="inventoryQty" label="Inventory qty" min={0} step={1} />
                <DateField control={form.control} name="dateInStock" label="Date in stock" />
                <DateField control={form.control} name="warrantyExpiresAt" label={isHardware ? "Warranty expires" : "License expires"} />
                <DateField control={form.control} name="dateSold" label="Date sold" />
              </Grid>
              <div className="mt-4">
                <TextAreaField control={form.control} name="notes" label="Notes" rows={2} />
              </div>
            </Panel>

            {error && <Notice tone="error">{error}</Notice>}

            <div className="flex items-center justify-end gap-3">
              <Button asChild variant="ghost">
                <Link to={backTo}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : isEdit ? "Save product" : "Create product"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function Grid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  const cls = cols === 3
    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
    : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
  return <div className={cls}>{children}</div>;
}

function blankProduct(): ProductDetail {
  return {
    id: "", type: "Hardware",
    sku: null, serialNumber: null, modelNumber: null,
    name: "", description: null, notes: null, licenseKey: null,
    cost: 0, priceLevel1: 0, priceLevel2: 0, priceLevel3: 0, priceLevel4: 0,
    inventoryQty: 0, barcode: null, manufacturer: null,
    dateInStock: null, warrantyExpiresAt: null, dateSold: null,
    status: "Active",
    createdAt: "", updatedAt: "",
  };
}
